import { useGetErrorInfo } from '@thoughtspot/blink-context';
import { useTranslation } from '@thoughtspot/i18n';
import { localStore } from '@thoughtspot/local-store';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { ChipInputProps } from '@thoughtspot/radiant-react/widgets/chip/chip-input';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useBoolean, useUpdateEffect } from 'react-use';
import { useAppContext } from '/@contexts/global-app-context';
import { ListType, Sharable } from '/@services/generated/graphql-types';
import { useUpdateDisplayName } from '/@services/session/session-service';
import { useGetSharablesQuery } from '/@services/share-permissions/share-permission-service';
import { useInviteUsersMutation } from '/@services/trial/trial-service';
import { BlinkErrorBoundary } from '/@utils/error-handling/blink-error-boundary';
import { getTranslatedInfoForMessageCode } from '/@utils/error-handling/error-strings-mapping';
import { getEmailDomain } from '../../email/email.util';
import { InviteUsersContentV2Container } from '../invite-users-content-v2/invite-users-content-v2.container';
import {
    InviteButtonState,
    InviteUsersModalV2ContainerProps,
} from '../invite-users-v2.interface';
import {
    getLiveboardPermission,
    getUniqueDataSourceIds,
    getValidEmailIds,
    setInviteButtonState,
} from '../invite-users-v2.util';
import { ShareObjectsContainer } from '../share-objects/share-objects.container';
import { UnderlyingDataSourcesContainer } from '../underlying-data-sources/underlying-data-sources.container';

export const MAX_MESSAGE_LENGTH = 500;

export const InviteUsersModalV2Container: React.FC<InviteUsersModalV2ContainerProps> = ({
    onModalCloseCb,
    setIsToastVisible,
    isShareStepEnabled,
    source,
}) => {
    const { t } = useTranslation();
    const { metricsService, sessionService } = useAppContext();
    const {
        data: sharables,
        loading: sharableLoading,
        error,
    } = useGetSharablesQuery(ListType.PinboardAnswerBook, 10, false);

    const {
        updateDisplayName,
        data: displayNameData,
        loading: displayNameLoading,
        error: displayNameError,
    } = useUpdateDisplayName();
    const [updatedDisplayName, setUpdateDisplayName] = useState<string>(
        sessionService.getUserDisplayName() || sessionService.getUserEmail(),
    );

    const {
        inviteUsers,
        data,
        loading: inviteLoading,
        error: inviteError,
    } = useInviteUsersMutation();

    // Control whether to show invite users modal, sharable list modal, underlying data sources modal
    const [isInviteModalOpen, setIsInviteModalOpen] = useBoolean(true);
    const [isShareModalOpen, setIsShareModalOpen] = useBoolean(false);
    const [isDataSourceModalOpen, setIsDataSourceModalOpen] = useBoolean(false);
    const [dataSourceIds, setDataSourceIds] = useState<string[]>([]);
    const [emailChips, setEmailChips] = useState<ChipInputProps[]>([]);
    const [comment, setComment] = useState<string>('');
    const [selectedSharableIds, setSelectedSharableIds] = useState<string[]>(
        [],
    );
    const [errorMsg, setErrorMsg] = useState('');
    const [isAlertBannerVisible, setIsAlertBannerVisible] = useBoolean(false);
    const [liveboardName, setLiveboardName] = useState('');
    const [numDataSourceShared, setNumDataSourceShared] = useState<number>(0);

    // Get any error obj set from parent Error Boundaries
    const { errors } = useGetErrorInfo();
    // Set showGlobalError to true when even the error object changes
    useEffect(() => {
        if (errors.length) {
            const upstreamErrorCode =
                errors?.[0].errorObj.extensions?.exception?.upstreamResponse
                    ?.data?.code;

            // Keys for error message in case of API errors
            const translatedErrMsgKeys = getTranslatedInfoForMessageCode(
                // Prioritise upstreamErrorCode over Generic UpstreamError code
                upstreamErrorCode || errors?.[0]?.code,
                'homehub.invite_others.error_message',
            );
            setErrorMsg(t(translatedErrMsgKeys.summary as any));
            setIsAlertBannerVisible(true);
        }
    }, [errors]);

    const onInviteModalDismiss = () => {
        setIsInviteModalOpen(false);
        onModalCloseCb();
    };
    const onShareModalDismiss = () => {
        setIsShareModalOpen(false);
        onModalCloseCb();
    };

    const resetErrorState = () => {
        setIsAlertBannerVisible(false);
        setErrorMsg('');
    };

    useUpdateEffect(() => {
        if (data) {
            setIsToastVisible();
            setIsShareModalOpen(false);
            setIsInviteModalOpen(false);
            const emailIds = getValidEmailIds(emailChips);
            const uniqueDomains = new Set<string>();
            emailIds.flatMap(email => uniqueDomains.add(getEmailDomain(email)));
            const uniqueDatasources = getUniqueDataSourceIds(sharables);
            metricsService.collectEvent(
                new UserAction(userActionTypeEnum.INVITE_OTHERS),
                {
                    numInvitedUsers: emailIds.length,
                    sharedGroupId: data.sharedGroupId,
                    userIds: data.userIds,
                    hasOptionalMessage: !!comment,
                    numLiveboardsShared: selectedSharableIds.length,
                    numDataSourceShared,
                    uniqueEmailDomains: Array.from(uniqueDomains),
                    numLiveboardsShown: sharables.length,
                    numDataSourceShown: uniqueDatasources.size,
                    source,
                },
            );
            onModalCloseCb();
        }
    }, [data]);

    useUpdateEffect(() => {
        if (!sharableLoading && !_.isUndefined(sharables)) {
            const uniqueDatasources = getUniqueDataSourceIds(sharables);
            metricsService.collectEvent(
                new UserAction(userActionTypeEnum.INVITE_SHARE_LOAD),
                {
                    numLiveboardsShown: sharables.length,
                    numDataSourceShown: uniqueDatasources.size,
                    source,
                },
            );
        }
    }, [sharableLoading]);

    useEffect(() => {
        if (data) {
            sessionService.triggerGlobalBannerObserver();
        }
    }, [inviteLoading, !!data]);

    const onConfirm = async () => {
        setInviteButtonState(InviteButtonState.INIT);
        // Emails which have error have metaIconConfig.
        // Filtering those out to just be left with emails
        const emailIds = getValidEmailIds(emailChips);
        // If there are any email with error we need the user to remove them
        // before we can proceed to the next screen.
        if (emailIds.length !== emailChips.length) {
            setIsAlertBannerVisible(true);
            const emailIdsWithErrors = emailChips?.length - emailIds?.length;
            setErrorMsg(
                emailIdsWithErrors === 1
                    ? t('invite.email.removeEmailsWithErrorsSingle')
                    : t('invite.email.removeEmailsWithErrorsMultiple'),
            );
            return;
        }
        // If no valid emails are there we need to show an error so that
        // user can enter atleast one valid email
        if (emailIds.length === 0) {
            setIsAlertBannerVisible(true);
            setErrorMsg(t('invite.email.noValidEmailError'));
            return;
        }
        if (_.isEmpty(updatedDisplayName.trim())) {
            setIsAlertBannerVisible(true);
            setErrorMsg(t('invite.email.noDisplayNameError'));
            return;
        }
        if (comment.length > MAX_MESSAGE_LENGTH) {
            setIsAlertBannerVisible(true);
            setErrorMsg(t('invite.modal.message.limitError'));
            return;
        }

        // If there are sharable objects then we need to show share dialog after invite
        if (
            isShareStepEnabled &&
            sharables &&
            sharables.length > 0 &&
            !isShareModalOpen
        ) {
            setIsShareModalOpen(true);
            const uniqueDatasources = getUniqueDataSourceIds(sharables);
            metricsService.collectEvent(
                new UserAction(userActionTypeEnum.INVITE_SHARE_OBJECT_LOAD),
                {
                    numLiveboardsShown: sharables.length,
                    numDataSourceShown: uniqueDatasources.size,
                    source,
                },
            );
            setIsInviteModalOpen(false);
            resetErrorState();
            return;
        }

        const sharableItems: Sharable[] = sharables
            .filter((sharable: Sharable) =>
                selectedSharableIds.includes(sharable.id),
            )
            .map((sharable: Sharable) => {
                const { __typename, ...sharableItem } = sharable;
                sharableItem.shareMode = getLiveboardPermission(
                    sharableItem.id,
                );
                return sharableItem;
            });

        const sharedDatasourceIds = _.uniq(
            sharableItems.flatMap(item => item.datasourceIds),
        );
        setNumDataSourceShared(sharedDatasourceIds.length);
        if (updatedDisplayName) {
            await updateDisplayName(updatedDisplayName);
            sessionService.setUserDisplayName(updatedDisplayName);
        }
        const shareableItems = isShareStepEnabled ? sharableItems : [];
        inviteUsers(emailIds, comment, isShareStepEnabled, {
            sharableItems: shareableItems,
        });
    };

    const onDataSourceClicked = (
        datasourceIds: string[],
        liveboard: string,
    ) => {
        setDataSourceIds(datasourceIds);
        setLiveboardName(liveboard);
        setIsDataSourceModalOpen(true);
        setIsShareModalOpen(false);
    };

    const dataSourceOnConfirmCb = () => {
        setIsShareModalOpen(true);
        setIsDataSourceModalOpen(false);
    };

    const handleNameInputChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setUpdateDisplayName(event.target.value);
    };

    return (
        <>
            {isInviteModalOpen && (
                // Adding this error boundary so that validate email errors do not propagate
                <BlinkErrorBoundary handledErrors={['*']}>
                    <InviteUsersContentV2Container
                        isModalOpen={isInviteModalOpen}
                        isLoading={
                            sharableLoading ||
                            inviteLoading ||
                            displayNameLoading
                        }
                        onConfirm={onConfirm}
                        onDismiss={onInviteModalDismiss}
                        emailChips={emailChips}
                        setEmailChips={setEmailChips}
                        sharables={sharables}
                        comment={comment}
                        setComment={setComment}
                        errorMsg={errorMsg}
                        isAlertBannerVisible={isAlertBannerVisible}
                        setIsAlertBannerVisible={resetErrorState}
                        displayName={updatedDisplayName}
                        handleNameInputChange={handleNameInputChange}
                        isShareStepEnabled={isShareStepEnabled}
                    />
                </BlinkErrorBoundary>
            )}
            {isShareModalOpen && (
                <ShareObjectsContainer
                    isShareModalOpen={isShareModalOpen}
                    onConfirm={onConfirm}
                    onDismiss={onShareModalDismiss}
                    objectList={sharables}
                    selectedSharableIds={selectedSharableIds}
                    setSelectedSharableIds={setSelectedSharableIds}
                    onDataSourceClicked={onDataSourceClicked}
                    isLoading={inviteLoading || displayNameLoading}
                    errorMsg={errorMsg}
                    isAlertBannerVisible={isAlertBannerVisible}
                    setIsAlertBannerVisible={setIsAlertBannerVisible}
                />
            )}
            {isDataSourceModalOpen && (
                <UnderlyingDataSourcesContainer
                    dataSourceIDs={dataSourceIds}
                    isDataSourceModalOpen={isDataSourceModalOpen}
                    pinboardName={liveboardName}
                    onConfirm={dataSourceOnConfirmCb}
                />
            )}
        </>
    );
};
